<template>
  <header class="fixed w-full z-100 bg-white border-b border-gray-200 h-16 flex items-center px-4 md:px-6 shadow-sm">
    <div class="flex justify-between items-center w-full gap-3 p-2">
      <div class="text-sm min-w-0">
        <Menubar :model="items" style="border:none;" />
      </div>

      <button
        type="button"
        class="shrink-0 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 p-1.5 hover:bg-gray-50 transition-colors cursor-pointer"
        @click="toggleUserMenu"
      >
        <Avatar
          :label="userInitials"
          shape="circle"
          class="bg-emerald-600 text-white font-semibold"
          size="large"
        />
        <div class="text-left leading-tight max-w-[220px]">
          <p class="text-sm font-semibold text-gray-900 truncate">{{ userName }}</p>
          <p class="text-xs text-gray-500 truncate">{{ userSubtext }}</p>
        </div>
        <i class="pi pi-angle-down text-gray-500 text-xs"></i>
      </button>

      <Menu ref="userMenuRef" :model="userMenuItems" popup />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import axios from './../lib/axios'
import { logout } from './../store/auth'

const toast = useToast()
const router = useRouter()
const userMenuRef = ref()
const role = ref<string>(localStorage.getItem('role') || '')
const userNameState = ref<string>(localStorage.getItem('user_name') || '')
const userEmailState = ref<string>(localStorage.getItem('user_email') || '')
const barcodeState = ref<string>(localStorage.getItem('barcode') || '')

const userName = computed(() => {
  if (userNameState.value) return userNameState.value
  return role.value === 'admin' ? 'Администратор' : 'Флорист'
})

const userSubtext = computed(() => {
  if (role.value === 'admin') {
    return userEmailState.value || barcodeState.value || 'admin'
  }
  return barcodeState.value ? `Barcode: ${barcodeState.value}` : 'employee'
})

const userInitials = computed(() => {
  const parts = userName.value.split(' ').filter(Boolean)
  if (!parts.length) return 'U'
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
})

const items = ref([
  { label: 'Dashboard', icon: 'pi pi-chart-line', command: () => router.push('/dashboard') },
  { label: 'Аналитика', icon: 'pi pi-chart-bar', command: () => router.push('/analytics') },
  { label: 'Текущие счета', icon: 'pi pi-credit-card', command: () => router.push('/account') },
  { label: 'Справочник товаров', icon: 'pi pi-shopping-cart', command: () => router.push('/product') },
  { label: 'Приход', icon: 'pi pi-truck', command: () => router.push('/arrival') },
  { label: 'Склад', icon: 'pi pi-warehouse', command: () => router.push('/store') },
  { label: 'Сотрудники', icon: 'pi pi-users', command: () => router.push('/employee') },
  { label: 'Клиенты', icon: 'pi pi-users', command: () => router.push('/clients') },
  { label: 'Справочники', icon: 'pi pi-book', command: () => router.push('/reference-page') },
  { label: 'Продажи', icon: 'pi pi-shopping-bag', command: () => router.push('/sale') },
  { label: 'Товары', icon: 'pi pi-shopping-cart', command: () => router.push('/product-list') },
  { label: 'Витрина', icon: 'pi pi-objects-column', command: () => router.push('/showcase') },
])

const applyRoleMenu = () => {
  if (role.value === 'employee') {
    const employeeMenu = new Set(['Dashboard', 'Продажи', 'Витрина', 'Товары', 'Клиенты'])
    items.value = items.value.filter((item) => employeeMenu.has(item.label))
    return
  }

  if (role.value === 'admin') {
    const adminMenu = new Set([
      'Аналитика',
      'Текущие счета',
      'Справочник товаров',
      'Приход',
      'Склад',
      'Сотрудники',
      'Клиенты',
      'Справочники'
    ])
    items.value = items.value.filter((item) => adminMenu.has(item.label))
  }
}

const doLogout = async () => {
  try {
    await logout()
    toast.add({ severity: 'success', summary: 'Выход', detail: 'Сессия завершена', life: 2000 })
    router.push({ name: 'MainPage' })
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: e?.response?.data?.message || 'Не удалось выйти', life: 2500 })
  }
}

const userMenuItems = computed(() => [
  {
    label: 'Выйти',
    icon: 'pi pi-sign-out',
    command: doLogout
  }
])

const toggleUserMenu = (event: Event) => {
  userMenuRef.value?.toggle(event)
}

onMounted(() => {
  applyRoleMenu()

  if (role.value === 'employee' && !userNameState.value && barcodeState.value) {
    axios.get('/api/get-employee', { params: { barcode: barcodeState.value } })
      .then((res) => {
        const employeeName = String(res?.data?.name || '').trim()
        if (employeeName) {
          userNameState.value = employeeName
          localStorage.setItem('user_name', employeeName)
        }
      })
      .catch(() => {})
  }
})
</script>
